import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Icon } from "@/components/Icon";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChrome } from "../ui/MobileShell";
import wordmark from "@/assets/pnrbet-wordmark.png";

/* MobileSignIn (design 8a) — brand up top, fields in the middle, every action in
   the lower half. REAL auth: POST /auth/login { email, password } →
   AuthContext.login(token, user).

   Credentials use the NORMAL OS keyboard + autofill + a11y (type=email /
   type=password, autoComplete, inputMode) — NEVER the numeric keypad.

   Social sign-in marks are OMITTED: the backend exposes no OAuth
   (login is email/password only), so we do NOT ship dead Apple/Google buttons
   (asset-replacement-map: render only if OAuth is wired, else omit). Full-height
   bare chrome. */

const GREEN = "#00D632";

function safeInternalPath(p, fallback = "/") {
  if (typeof p !== "string" || !p.startsWith("/") || p.startsWith("//") || p.startsWith("/\\")) return fallback;
  if (p === "/login" || p === "/signin") return fallback;
  return p;
}

export default function MobileSignIn() {
  useChrome({ bare: true });
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const returnTo = safeInternalPath(location.state?.from);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !password) { setErr("Enter your email and password."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      navigate(returnTo, { replace: true });
    } catch (ex) {
      setErr(apiError(ex?.response?.data?.detail) || "Could not sign in. Check your details and try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="m-app" style={{ position: "fixed", inset: 0, zIndex: 50, overflowY: "auto",
      background: "radial-gradient(120% 60% at 50% 0%,rgba(0,214,50,.1) 0%,rgba(0,214,50,0) 55%),#0A0A0A",
      paddingTop: "calc(40px + var(--m-safe-top))" }}>
      <form onSubmit={submit} style={{ minHeight: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column", padding: "0 20px calc(34px + var(--m-safe-bot))" }}>
        {/* brand */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 20 }}>
          <img src={wordmark} alt="PRYMEBET" style={{ height: 30, width: "auto", display: "block", filter: "drop-shadow(0 6px 16px rgba(0,0,0,.5))" }} />
          <div style={{ font: "800 30px 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>Welcome back</div>
        </div>

        {/* fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 28 }}>
          <div>
            <label htmlFor="m-signin-email" style={labelStyle}>Email</label>
            <div style={fieldWrap}>
              <input id="m-signin-email" type="email" inputMode="email" autoComplete="email"
                autoCapitalize="none" autoCorrect="off" spellCheck={false} enterKeyHint="next"
                value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                style={inputStyle} />
            </div>
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <label htmlFor="m-signin-password" style={labelStyle}>Password</label>
              <Link to="/login?mode=forgot" style={{ font: "600 12px 'Outfit'", color: "#a1a1aa", textDecoration: "none" }}>Forgot password?</Link>
            </div>
            <div style={fieldWrap}>
              <input id="m-signin-password" type={show ? "text" : "password"} autoComplete="current-password"
                enterKeyHint="go" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                style={inputStyle} />
              <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"}
                style={{ flex: "none", background: "none", border: 0, cursor: "pointer", font: "600 13px 'Outfit'", color: "#a1a1aa" }}>
                {show ? "Hide" : "Show"}
              </button>
            </div>
          </div>
          {err && <div role="alert" style={{ font: "600 13px 'Outfit'", color: "#EF4444" }}>{err}</div>}
        </div>

        {/* lower half actions */}
        <div style={{ flex: 1 }} />
        <button type="submit" disabled={busy}
          style={{ width: "100%", height: 56, borderRadius: 16, background: GREEN, color: "#0A0A0A", font: "800 16px 'Manrope'", border: 0, cursor: busy ? "default" : "pointer", opacity: busy ? 0.7 : 1, boxShadow: "inset 0 1px 0 rgba(255,255,255,.3)", marginTop: 28 }}>
          {busy ? "Signing in…" : "Sign in"}
        </button>

        <p style={{ textAlign: "center", font: "500 14px 'Outfit'", color: "#a1a1aa", marginTop: 18 }}>
          New to PRYMEBET?{" "}
          <Link to="/register" style={{ font: "700 14px 'Outfit'", color: GREEN, textDecoration: "none" }}>Create account</Link>
        </p>
        <p style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, font: "500 11px 'Outfit'", color: "#71717a", marginTop: 16 }}>
          <Icon name="lock" size={12} style={{ color: "#71717a" }} /> 18+ · No purchase necessary · Void where prohibited
        </p>
      </form>
    </div>
  );
}

const labelStyle = { display: "block", font: "600 11px 'Outfit'", letterSpacing: ".04em", color: "#a1a1aa", marginBottom: 7 };
const fieldWrap = {
  display: "flex", alignItems: "center", gap: 10, height: 56, boxSizing: "border-box", padding: "0 16px",
  borderRadius: 16, background: "#111114", boxShadow: "inset 0 0 0 1px #26262B",
};
const inputStyle = {
  flex: 1, minWidth: 0, height: "100%", background: "transparent", border: 0, outline: "none",
  color: "#fff", font: "600 16px 'Outfit'",
};
